import React, { createContext, useContext, useEffect, useState } from 'react';
import { 
  collection, 
  doc, 
  getDoc, 
  getDocs,
  addDoc,
  updateDoc,
  serverTimestamp 
} from 'firebase/firestore';
import { db } from '../firebaseconf';
import { useAuth } from './AuthContext';

const InstitutionContext = createContext();

export const useInstitution = () => {
  const context = useContext(InstitutionContext);
  if (!context) {
    throw new Error('useInstitution must be used within InstitutionProvider');
  }
  return context;
};

export const InstitutionProvider = ({ children }) => {
  const { currentUser } = useAuth();
  const [currentInstitution, setCurrentInstitution] = useState(null);
  const [institutions, setInstitutions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch current institution based on user
  useEffect(() => {
    if (!currentUser) {
      setLoading(false);
      return;
    }

    const fetchInstitution = async () => {
      try {
        setLoading(true);
        setError(null);

        // Get institution_id from user
        const institutionId = currentUser.institution_id || currentUser.institutionId;

        if (!institutionId) {
          // For super admin, fetch all institutions
          if (currentUser.role === 'super_admin') {
            const instCollection = collection(db, 'institutions');
            const snapshot = await getDocs(instCollection);
            const insts = snapshot.docs.map(doc => ({
              id: doc.id,
              ...doc.data()
            }));
            setInstitutions(insts);
          }
          setLoading(false);
          return;
        }

        // Fetch specific institution
        const instRef = doc(db, 'institutions', institutionId);
        const instSnap = await getDoc(instRef);

        if (instSnap.exists()) {
          setCurrentInstitution({
            id: instSnap.id,
            ...instSnap.data()
          });
        }

        setLoading(false);
      } catch (err) {
        console.error('Error fetching institution:', err);
        setError(err.message);
        setLoading(false);
      }
    };

    fetchInstitution();
  }, [currentUser]);

  const createInstitution = async (institutionData) => {
    try {
      setError(null);
      const docRef = await addDoc(collection(db, 'institutions'), {
        ...institutionData,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        status: 'active',
        subscription_status: 'pending',
        current_users: 1, // Admin
        current_storage_gb: 0
      });
      return docRef.id;
    } catch (err) {
      console.error('Error creating institution:', err);
      setError(err.message);
      throw err;
    }
  };

  const updateInstitution = async (institutionId, updates) => {
    try {
      setError(null);
      const instRef = doc(db, 'institutions', institutionId);
      await updateDoc(instRef, {
        ...updates,
        updatedAt: serverTimestamp()
      });

      // Update local state
      if (currentInstitution?.id === institutionId) {
        setCurrentInstitution(prev => ({
          ...prev,
          ...updates,
          updatedAt: new Date()
        }));
      }
    } catch (err) {
      console.error('Error updating institution:', err);
      setError(err.message);
      throw err;
    }
  };

  const fetchBranches = async (institutionId) => {
    try {
      const branchRef = collection(db, 'institutions', institutionId, 'branches');
      const snapshot = await getDocs(branchRef);
      return snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
    } catch (err) {
      console.error('Error fetching branches:', err);
      throw err;
    }
  };

  const fetchClasses = async (institutionId) => {
    try {
      const classRef = collection(db, 'institutions', institutionId, 'classes');
      const snapshot = await getDocs(classRef);
      return snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
    } catch (err) {
      console.error('Error fetching classes:', err);
      throw err;
    }
  };

  const createBranch = async (institutionId, branchData) => {
    try {
      const branchRef = collection(db, 'institutions', institutionId, 'branches');
      const docRef = await addDoc(branchRef, {
        ...branchData,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        status: 'active'
      });
      return docRef.id;
    } catch (err) {
      console.error('Error creating branch:', err);
      throw err;
    }
  };

  const createClass = async (institutionId, classData) => {
    try {
      const classRef = collection(db, 'institutions', institutionId, 'classes');
      const docRef = await addDoc(classRef, {
        ...classData,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        status: 'active'
      });
      return docRef.id;
    } catch (err) {
      console.error('Error creating class:', err);
      throw err;
    }
  };

  const value = {
    currentInstitution,
    institutions,
    loading,
    error,
    createInstitution,
    updateInstitution,
    fetchBranches,
    fetchClasses,
    createBranch,
    createClass
  };

  return (
    <InstitutionContext.Provider value={value}>
      {children}
    </InstitutionContext.Provider>
  );
};
